import { useReducer, useRef } from 'react'
import useAuth from '@/features/auth/hooks/use-auth'
import {
  canLoadMore,
  initialOlderEventsState,
  olderEventsReducer
} from '../models/client-account-older-events'
import {
  type ClientAccountEventV2,
  clientAccountsAdminV2Service
} from '../services/client-accounts-admin-v2.service'

type FirstPage = { hasMore: boolean; nextCursor: string | null }

/**
 * Las páginas MÁS VIEJAS de la bitácora («Ver más»).
 *
 * La primera página viene dentro del detalle; acá sólo se acumulan las siguientes. El estado es
 * local y NO una query a propósito: un cursor es un punto en el tiempo atado a la lectura que
 * lo emitió, y cachearlo en la familia `clientAccounts` lo dejaría sobrevivir a la
 * invalidación del detalle. Quien monta este hook lo hace con una `key` que cambia cuando el
 * detalle trae una primera página distinta, y así las páginas acumuladas se descartan solas.
 * Las reglas viven en `olderEventsReducer`, que tiene sus propias pruebas.
 */
export function useClientAccountOlderEvents(accountId: string, first: FirstPage) {
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  const [state, dispatch] = useReducer(
    olderEventsReducer<ClientAccountEventV2>,
    first,
    initialOlderEventsState<ClientAccountEventV2>
  )
  // Corta el doble toque antes de que el render refleje `loading`.
  const inFlight = useRef(false)

  const loadMore = async () => {
    if (inFlight.current || !canLoadMore(state) || state.cursor === null) return
    inFlight.current = true
    dispatch({ type: 'start' })
    try {
      const page = await clientAccountsAdminV2Service.events(accessToken, accountId, {
        before: state.cursor
      })
      dispatch({ type: 'loaded', page })
    } catch {
      dispatch({ type: 'failed' })
    } finally {
      inFlight.current = false
    }
  }

  return {
    older: state.older,
    hasMore: state.cursor !== null,
    isLoading: state.status === 'loading',
    isError: state.status === 'error',
    loadMore: () => {
      void loadMore()
    }
  }
}
